/**
 * Admin · Topic suggestions.
 *
 * Read-only list of topics the community has suggested via the Telegram
 * bot. Sorted by vote count desc — the loudest demand is at the top.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

const PAGE_SIZES = [25, 50, 100];

const state = {
  pageSize: 25,
  offset: 0,
  total: 0,
  hasMore: false,
};

export async function renderAdminSuggestions(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Suggestions (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Topic suggestions</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad" id="suggestions-page">
      <div class="card" style="padding:12px;">
        <div class="muted" style="font-size:12px;">
          Submitted by community members via the Telegram bot's <code>/suggest</code>
          command. Each duplicate submission boosts the existing topic instead of
          creating a new row, so the vote count reflects how many distinct
          requests the topic received.
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;margin-top:10px;">
          <label>
            <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Per page</div>
            <select id="filter-size">
              ${PAGE_SIZES.map((n) => `<option value="${n}" ${state.pageSize === n ? 'selected' : ''}>${n}</option>`).join('')}
            </select>
          </label>
        </div>
      </div>

      <div class="card" style="padding:12px;margin-top:10px;" id="suggestions-card">
        <div id="suggestions-summary" class="muted" style="font-size:11px;margin-bottom:6px;"></div>
        <div id="suggestions-list"><div class="muted">Loading…</div></div>
        <div id="suggestions-pager" class="row" style="justify-content:space-between;margin-top:10px;"></div>
      </div>
    </div>
  `;

  document.getElementById('filter-size').addEventListener('change', (e) => {
    state.pageSize = Number(e.target.value);
    state.offset = 0;
    void loadSuggestions();
  });

  await loadSuggestions();
}

async function loadSuggestions() {
  const list = document.getElementById('suggestions-list');
  const pager = document.getElementById('suggestions-pager');
  const summary = document.getElementById('suggestions-summary');
  list.innerHTML = `<div class="muted">Loading…</div>`;
  pager.innerHTML = '';
  try {
    const params = new URLSearchParams({
      limit: String(state.pageSize),
      offset: String(state.offset),
    });
    const page = await api(`/admin/suggestions?${params.toString()}`);
    state.total = page.total;
    state.hasMore = page.has_more;
    if (page.total === 0) {
      summary.textContent = 'No suggestions yet.';
    } else {
      summary.textContent = `${page.total.toLocaleString('en-NG')} topic${page.total === 1 ? '' : 's'} · showing ${page.offset + 1}–${page.offset + page.items.length}`;
    }
    renderList(list, page.items);
    renderPager(pager);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    list.innerHTML = `<div style="color:var(--color-danger)">${escape(err.message || 'Could not load suggestions.')}</div>`;
  }
}

function renderList(host, items) {
  if (!items.length) {
    host.innerHTML = `<div class="muted">No suggestions on this page.</div>`;
    return;
  }
  host.innerHTML = `
    <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="text-align:left;color:#aaa;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;">
            <th style="padding:6px 8px;">Topic</th>
            <th style="padding:6px 8px;white-space:nowrap;">Votes</th>
            <th style="padding:6px 8px;">Recent suggesters</th>
            <th style="padding:6px 8px;white-space:nowrap;">Last suggested</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(renderRow).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function renderRow(row) {
  const recent = (row.recent_suggesters || []).map((s) => escape(s)).join(', ');
  return `
    <tr style="border-top:1px solid #1a1432;">
      <td style="padding:8px;color:#fff;">${escape(row.display_text)}</td>
      <td style="padding:8px;white-space:nowrap;font-family:var(--font-display);font-weight:700;color:#fff;">${row.vote_count}</td>
      <td style="padding:8px;color:#bbb;">${recent || '<span class="muted">—</span>'}</td>
      <td style="padding:8px;white-space:nowrap;">${formatWhen(row.last_suggested_at)}</td>
    </tr>
  `;
}

function renderPager(host) {
  const onFirst = state.offset === 0;
  host.innerHTML = `
    <button type="button" class="button-secondary button-inline" id="prev-page" ${onFirst ? 'disabled' : ''}>← Prev</button>
    <div class="muted" style="font-size:11px;">Page ${Math.floor(state.offset / state.pageSize) + 1}</div>
    <button type="button" class="button-secondary button-inline" id="next-page" ${state.hasMore ? '' : 'disabled'}>Next →</button>
  `;
  host.querySelector('#prev-page').addEventListener('click', () => {
    state.offset = Math.max(0, state.offset - state.pageSize);
    void loadSuggestions();
  });
  host.querySelector('#next-page').addEventListener('click', () => {
    state.offset += state.pageSize;
    void loadSuggestions();
  });
}

function formatWhen(iso) {
  if (!iso) return `<span class="muted">—</span>`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return `<span class="muted">—</span>`;
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - d.getTime()) / 1000));
  let rel;
  if (diffSec < 60) rel = 'just now';
  else if (diffSec < 3600) rel = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) rel = `${Math.floor(diffSec / 3600)}h ago`;
  else if (diffSec < 30 * 86400) rel = `${Math.floor(diffSec / 86400)}d ago`;
  else rel = d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
  const abs = d.toLocaleString('en-NG', { timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short' });
  return `<span title="${escape(abs)}">${escape(rel)}</span>`;
}
